"use client"

import { createContext, useContext, useRef, useState } from "react"
import { track, Events } from "@/lib/analytics"

const FIRST_UPLOAD_KEY = "db_first_upload_tracked"

type UploadStatus = "idle" | "pending" | "processing" | "done" | "error"

// "pending" is for files staged before upload starts — currently used
// by the /try → /dashboard resume flow, where the dashboard discovers
// a CSV the user dropped anonymously and needs an explicit OK to send
// it through the worker.
type PendingMeta = {
  fileName:    string
  fileSize:    number
  rowEstimate: number
  source:      "try-preview"
  onConfirm:   () => void
  onDiscard:   () => void
}

type UploadContextType = {
  status:       UploadStatus
  progress:     number
  fileName:     string | null
  jobId:        string | null
  startedAt:    number | null
  errorMessage: string | null
  notice:       string | null  // optional one-liner inside the card
  pending:      PendingMeta | null

  startUpload:    (fileName: string, opts?: { notice?: string }) => void
  setProgress:    (n: number | ((prev: number) => number)) => void
  finishUpload:   () => void
  failUpload:     (message?: string) => void
  setJob:         (jobId: string) => void
  dismissStatus:  () => void

  /** Stage a file for explicit user confirmation before it touches the
   *  worker. Currently invoked by PendingUploadProcessor when /try
   *  drops a CSV in sessionStorage. */
  setPending:     (meta: PendingMeta | null) => void
}

const UploadContext = createContext<UploadContextType | null>(null)

export function UploadProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus]       = useState<UploadStatus>("idle")
  const [progress, setProgress]   = useState(0)
  const [fileName, setFileName]   = useState<string | null>(null)
  const [jobId, setJobId]         = useState<string | null>(null)
  const [startedAt, setStartedAt] = useState<number | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [notice, setNotice]       = useState<string | null>(null)
  const [pending, setPendingState] = useState<PendingMeta | null>(null)

  // Timer refs so dismissStatus() can clear the auto-idle timeouts.
  const doneTimer  = useRef<ReturnType<typeof setTimeout> | null>(null)
  const errorTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimers = () => {
    if (doneTimer.current)  { clearTimeout(doneTimer.current);  doneTimer.current  = null }
    if (errorTimer.current) { clearTimeout(errorTimer.current); errorTimer.current = null }
  }

  const resetToIdle = () => {
    clearTimers()
    setStatus("idle")
    setProgress(0)
    setFileName(null)
    setStartedAt(null)
    setErrorMessage(null)
    setNotice(null)
    setPendingState(null)
  }

  /** Wraps the raw pending setter so passing a meta flips the status
   *  to "pending" and clearing returns to idle. Keeps consumers from
   *  having to set both fields atomically.
   *
   *  Internal callers (startUpload, finishUpload, failUpload) clear
   *  pending via the raw setPendingState directly to avoid disturbing
   *  their own status transitions. This entry point is reserved for
   *  user-initiated flows — staging from /try and the explicit
   *  "Not now" discard. */
  const setPending = (meta: PendingMeta | null) => {
    if (meta) {
      setPendingState(meta)
      setStatus("pending")
      setFileName(meta.fileName)
    } else {
      // Unconditional reset. An earlier version gated the status flip
      // on `if (status === "pending")` to avoid clobbering other
      // states, but `status` was a stale closure value captured when
      // setPending was created — so the discard callback from
      // PendingUploadProcessor (created during the idle render) read
      // "idle" and never fired the reset, leaving the chip stuck on
      // "pending" with an empty body.
      setPendingState(null)
      setStatus("idle")
      setFileName(null)
    }
  }

  const startUpload = (name: string, opts?: { notice?: string }) => {
    clearTimers()
    setPendingState(null)
    setFileName(name)
    setProgress(0)
    setErrorMessage(null)
    setNotice(opts?.notice ?? null)
    setStartedAt(Date.now())
    setStatus("processing")
  }

  const finishUpload = () => {
    setProgress(100)
    setStatus("done")

    if (typeof window !== "undefined" && !window.localStorage.getItem(FIRST_UPLOAD_KEY)) {
      track(Events.FIRST_UPLOAD_COMPLETED, fileName ? { fileName } : undefined)
      window.localStorage.setItem(FIRST_UPLOAD_KEY, "1")
    }

    // Auto-dismiss the success card after a short beat. User can close
    // earlier via the × button.
    doneTimer.current = setTimeout(resetToIdle, 2500)
  }

  const failUpload = (message?: string) => {
    clearTimers()
    if (message) setErrorMessage(message)
    setStatus("error")
    // Errors stay on screen longer — the user needs a chance to read the
    // message and click Retry if they want.
    errorTimer.current = setTimeout(resetToIdle, 8000)
  }

  const setJob = (id: string) => {
    setJobId(id)
  }

  return (
    <UploadContext.Provider
      value={{
        status,
        progress,
        fileName,
        jobId,
        startedAt,
        errorMessage,
        notice,
        pending,
        startUpload,
        setProgress,
        finishUpload,
        failUpload,
        setJob,
        dismissStatus: resetToIdle,
        setPending,
      }}
    >
      {children}
    </UploadContext.Provider>
  )
}

export function useUploadContext() {
  const ctx = useContext(UploadContext)
  if (!ctx) throw new Error("UploadContext missing")
  return ctx
}